import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { SlidersHorizontal, Save, Lock, Mail, Send, Image as ImageIcon, Upload, Calculator, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import StationRoles from "@/components/StationRoles";
import { Armchair } from "lucide-react";

function Field({ label, children, hint }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.15em] text-slate-400 font-semibold">{label}</label>
      {children}
      {hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

const input = "mt-2 w-full rounded-lg bg-[#171E2E] border border-[#2A3550] px-4 py-2.5 text-sm focus:border-zinc-500 focus:outline-none transition-colors duration-200";

const LANDING_IMAGES = [
  { key: "smoky", label: "Barbershop · Smoky", url: "https://images.unsplash.com/photo-1536520002442-39764a41e987?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" },
  { key: "brick", label: "Barbershop · Brick", url: "https://images.unsplash.com/photo-1675599193990-33d71150902b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" },
  { key: "modern", label: "Modern Salon", url: "https://images.unsplash.com/photo-1626383137804-ff908d2753a2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" },
  { key: "luxe", label: "Luxe Salon", url: "https://images.unsplash.com/photo-1706629505300-168aa1604912?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" },
  { key: "boutique", label: "Boutique Salon", url: "https://images.unsplash.com/photo-1600948836101-f9ffda59d250?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" },
  { key: "spa", label: "Day Spa", url: "https://images.unsplash.com/photo-1693578538512-fc66f318c833?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" },
];

export default function Settings() {
  const { user } = useAuth();
  const { apply, refresh, branding } = useBranding();
  const readOnly = user?.role !== "corporate";

  const qc = useQueryClient();
  const { data: routeStores } = useQuery({ queryKey: ["stores"], queryFn: () => api.get("/stores").then((r) => r.data) });
  const [emails, setEmails] = useState({});
  const [cfg, setCfg] = useState({});
  const [uploading, setUploading] = useState(false);
  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/branding/login-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBrand((b) => ({ ...b, login_image: data.url }));
      refresh();
      toast.success("Landing photo uploaded");
    } catch { toast.error("Upload failed — use a JPG/PNG/WEBP under 6MB"); }
    setUploading(false);
    e.target.value = "";
  };
  const saveManager = async (s) => {
    try {
      const c = cfg[s.id] || {};
      const payload = { manager_email: emails[s.id] ?? s.manager_email ?? "" };
      if (c.leakage_threshold !== undefined) payload.leakage_threshold = Number(c.leakage_threshold);
      if (c.recap_day !== undefined) payload.recap_day = Number(c.recap_day);
      if (c.recap_hour !== undefined) payload.recap_hour = Number(c.recap_hour);
      await api.put(`/stores/${s.id}`, payload);
      toast.success(`Saved settings for #${s.number}`);
      qc.invalidateQueries({ queryKey: ["stores"] });
    } catch { toast.error("Not authorized for this store"); }
  };
  const sendRecap = async () => {
    try {
      const { data } = await api.post("/recap/send", {});
      toast.success(`Weekly recap sent for ${data.sent} store${data.sent !== 1 ? "s" : ""}`);
      qc.invalidateQueries({ queryKey: ["alerts"] });
    } catch { toast.error("Could not send recap"); }
  };

  const [brand, setBrand] = useState(null);
  const [thr, setThr] = useState(null);

  const { data: brandData } = useQuery({ queryKey: ["branding-settings"], queryFn: () => api.get("/branding").then((r) => r.data) });
  const { data: thrData } = useQuery({ queryKey: ["thresholds-settings"], queryFn: () => api.get("/thresholds").then((r) => r.data) });

  useEffect(() => { if (brandData) setBrand({ login_image: "", login_headline: "", login_subtext: "", ...brandData }); }, [brandData]);
  useEffect(() => { if (thrData) setThr(thrData); }, [thrData]);

  // Maintenance-report schedule (routine cadence; urgent battery/tamper always sends)
  const [maint, setMaint] = useState(null);
  const { data: maintData } = useQuery({ queryKey: ["maint-prefs"], queryFn: () => api.get("/health/maintenance-prefs").then((r) => r.data) });
  useEffect(() => { if (maintData) setMaint(maintData); }, [maintData]);
  const saveMaint = async () => {
    try { const { data } = await api.put("/health/maintenance-prefs", maint); setMaint(data); toast.success("Maintenance schedule saved"); }
    catch { toast.error("Could not save maintenance schedule"); }
  };

  const saveBranding = async () => {
    try {
      const { data } = await api.put("/branding", {
        ...brand,
        target: branding.target,
        currency: branding.currency,
        currency_symbol: branding.currency_symbol,
      });
      apply(data);
      toast.success("Branding updated — the whole app just re-skinned");
    } catch (e) { toast.error("Corporate access required to change branding"); }
  };

  const onNum = (key) => (e) => {
    const v = e.target.value.replace(/[^0-9]/g, "").replace(/^0+(?=\d)/, "");
    setThr({ ...thr, [key]: v });
  };

  const saveThresholds = async () => {
    try {
      const keys = ["weight_gate_lb", "stage1_min", "sale_engage_min", "extended_min", "video_retention_days", "leakage_alert_threshold"];
      const payload = { ...thr };
      keys.forEach((k) => { if (payload[k] !== undefined) payload[k] = Number(payload[k]) || 0; });
      await api.put("/thresholds", payload);
      setThr(payload);
      toast.success("Thresholds saved");
    } catch (e) { toast.error("Corporate access required"); }
  };

  // Accounting options (per-user profile — accountant + auto month-end summary)
  const [acct, setAcct] = useState(null);
  const { data: profData } = useQuery({ queryKey: ["profile-accounting"], queryFn: () => api.get("/profile").then((r) => r.data) });
  useEffect(() => { if (profData) setAcct(profData); }, [profData]);
  const saveAccounting = async () => {
    try { await api.put("/profile", acct); toast.success("Accounting options saved"); }
    catch { toast.error("Could not save accounting options"); }
  };
  const sendMonthlyNow = async () => {
    if (!acct?.accountant_email) { toast.error("Add your accountant's email first"); return; }
    try {
      await api.put("/profile", acct);
      const { data } = await api.post("/profile/send-monthly-summary");
      if (data.sent) toast.success(`Monthly summary emailed for ${data.period}`);
      else toast.error("Summary could not be sent (check the accountant email)");
    } catch { toast.error("Summary could not be sent"); }
  };

  if (!brand || !thr) return <div className="text-slate-400">Loading…</div>;

  return (
    <div>
      <div className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold">Settings</div>
      <h1 className="mt-2 font-display text-3xl md:text-4xl font-light tracking-tight">Configure your platform</h1>
      {readOnly && (
        <div className="mt-4 inline-flex items-center gap-2 text-sm text-amber-400 bg-amber-500/100/10 border border-amber-500/30 rounded-lg px-4 py-2">
          <Lock className="h-4 w-4" /> View only — corporate access is required to make changes.
        </div>
      )}

      <Tabs defaultValue="branding" className="mt-8">
        <TabsList className="flex flex-wrap gap-2 bg-transparent border-0 p-0 h-auto justify-start">
          <TabsTrigger value="branding" data-testid="tab-branding" className="rounded-full border border-[#2A3550] bg-[#1E2638] px-4 py-2 text-slate-300 data-[state=active]:bg-[var(--brand)] data-[state=active]:text-white data-[state=active]:border-transparent transition-colors duration-200"><ImageIcon className="h-4 w-4 mr-2" /> Landing Page</TabsTrigger>
          <TabsTrigger value="thresholds" data-testid="tab-thresholds" className="rounded-full border border-[#2A3550] bg-[#1E2638] px-4 py-2 text-slate-300 data-[state=active]:bg-[var(--brand)] data-[state=active]:text-white data-[state=active]:border-transparent transition-colors duration-200"><SlidersHorizontal className="h-4 w-4 mr-2" /> Thresholds</TabsTrigger>
          <TabsTrigger value="routing" data-testid="tab-routing" className="rounded-full border border-[#2A3550] bg-[#1E2638] px-4 py-2 text-slate-300 data-[state=active]:bg-[var(--brand)] data-[state=active]:text-white data-[state=active]:border-transparent transition-colors duration-200"><Mail className="h-4 w-4 mr-2" /> Alerts & Routing</TabsTrigger>
          <TabsTrigger value="accounting" data-testid="tab-accounting" className="rounded-full border border-[#2A3550] bg-[#1E2638] px-4 py-2 text-slate-300 data-[state=active]:bg-[var(--brand)] data-[state=active]:text-white data-[state=active]:border-transparent transition-colors duration-200"><Calculator className="h-4 w-4 mr-2" /> Accounting</TabsTrigger>
          <TabsTrigger value="stations" data-testid="tab-stations" className="rounded-full border border-[#2A3550] bg-[#1E2638] px-4 py-2 text-slate-300 data-[state=active]:bg-[var(--brand)] data-[state=active]:text-white data-[state=active]:border-transparent transition-colors duration-200"><Armchair className="h-4 w-4 mr-2" /> Stations</TabsTrigger>
        </TabsList>

        {/* Landing page */}
        <TabsContent value="branding" className="mt-6">
          {/* Landing page image */}
          <div className="surface-card p-6 space-y-5" data-testid="landing-settings">
            <div>
              <h2 className="font-display text-lg font-medium">Landing page</h2>
              <p className="text-xs text-slate-400 mt-1">Pick the front-facing image your prospects see at sign-in — match it to the client (barbershop, salon, spa).</p>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.15em] text-slate-400 font-semibold mb-3">Front image</div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {LANDING_IMAGES.map((im) => {
                  const active = (brand.login_image || LANDING_IMAGES[0].url) === im.url;
                  return (
                    <button key={im.url} disabled={readOnly} onClick={() => setBrand({ ...brand, login_image: im.url })}
                      data-testid={`landing-img-${im.key}`}
                      className={`relative rounded-lg overflow-hidden border-2 transition-all duration-200 disabled:opacity-50 ${active ? "border-[var(--brand)]" : "border-transparent hover:border-[#334063]"}`}>
                      <img src={im.url} alt={im.label} className="h-14 w-full object-cover" />
                      <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[9px] py-0.5 text-center truncate px-1">{im.label}</span>
                    </button>
                  );
                })}
              </div>
              <div className="mt-3 flex items-center gap-3 flex-wrap">
                <label className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium border border-[#2A3550] bg-[#1E2638] transition-colors duration-200 ${readOnly ? "opacity-50 cursor-not-allowed" : "hover:border-[#38BDF8] cursor-pointer text-slate-100"}`}>
                  <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Upload your own photo"}
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" data-testid="landing-upload-input" disabled={readOnly || uploading} onChange={handleUpload} />
                </label>
                {brand.login_image && !LANDING_IMAGES.some((im) => im.url === brand.login_image) && (
                  <span className="inline-flex items-center gap-2 text-xs text-emerald-400" data-testid="landing-custom-active">
                    <img src={brand.login_image} alt="custom" className="h-8 w-12 object-cover rounded border border-[#2A3550]" /> Custom photo active
                  </span>
                )}
              </div>
            </div>
            <button disabled={readOnly} onClick={saveBranding} data-testid="save-landing-btn"
              className="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-medium text-white transition-opacity duration-200 hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: "var(--brand)" }}>
              <Save className="h-4 w-4" /> Save landing page
            </button>
          </div>
        </TabsContent>

        {/* Thresholds */}
        <TabsContent value="thresholds" className="mt-6">
          <div className="surface-card p-6 space-y-6 max-w-3xl">
            <h2 className="font-display text-lg font-medium">Staged threshold model</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Weight gate (lb)" hint="Minimum weight to register occupancy">
                <input type="text" inputMode="numeric" disabled={readOnly} className={input} value={thr.weight_gate_lb} data-testid="thr-weight-gate"
                  onChange={onNum("weight_gate_lb")} />
              </Field>
              <Field label="Stage 1 — engagement (min)" hint="First occupancy registered">
                <input type="text" inputMode="numeric" disabled={readOnly} className={input} value={thr.stage1_min} data-testid="thr-stage1"
                  onChange={onNum("stage1_min")} />
              </Field>
              <Field label="Sale engages (min)" hint="Registers as a service event">
                <input type="text" inputMode="numeric" disabled={readOnly} className={input} value={thr.sale_engage_min} data-testid="thr-sale-engage"
                  onChange={onNum("sale_engage_min")} />
              </Field>
              <Field label="Extended service after (min)">
                <input type="text" inputMode="numeric" disabled={readOnly} className={input} value={thr.extended_min} data-testid="thr-extended"
                  onChange={onNum("extended_min")} />
              </Field>
              <Field label="Video retention (days)" hint="Flagged clips auto-purge after">
                <input type="text" inputMode="numeric" disabled={readOnly} className={input} value={thr.video_retention_days} data-testid="thr-retention"
                  onChange={onNum("video_retention_days")} />
              </Field>
              <Field label="Leakage alert threshold" hint="Alert when a store crosses this loss">
                <input type="text" inputMode="numeric" disabled={readOnly} className={input} value={thr.leakage_alert_threshold} data-testid="thr-alert"
                  onChange={onNum("leakage_alert_threshold")} />
              </Field>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-[#2A3550] bg-[#171E2E] px-4 py-3">
              <div>
                <div className="text-sm font-medium">Face blur by default</div>
                <div className="text-xs text-slate-400">Automatically blur customer faces in all clips</div>
              </div>
              <Switch checked={thr.face_blur_enabled} disabled={readOnly} data-testid="thr-face-blur"
                onCheckedChange={(v) => setThr({ ...thr, face_blur_enabled: v })} />
            </div>

            <button disabled={readOnly} onClick={saveThresholds} data-testid="save-thresholds-btn"
              className="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-medium text-white transition-opacity duration-200 hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: "var(--brand)" }}>
              <Save className="h-4 w-4" /> Save thresholds
            </button>
          </div>
        </TabsContent>

        <TabsContent value="routing" className="mt-6">
          <div className="surface-card p-6 space-y-5 max-w-3xl mb-6" data-testid="maintenance-settings">
            <div className="flex items-center gap-2"><CalendarClock className="h-5 w-5 text-[#38BDF8]" /><h2 className="font-display text-lg font-medium">Maintenance reports</h2></div>
            <p className="text-xs text-slate-400">Choose how often owners &amp; managers get the seat-health digest. Whatever you pick, an <strong className="text-slate-200">urgent alert is always sent the same morning</strong> if a battery dies overnight or a seat is tampered with.</p>
            {maint ? (
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="How often" hint="Routine digest cadence">
                  <select className={input} data-testid="maint-frequency" value={maint.frequency}
                    onChange={(e) => setMaint({ ...maint, frequency: e.target.value })}>
                    <option value="weekly">Weekly (recommended)</option>
                    <option value="daily">Daily</option>
                    <option value="off">Only urgent alerts</option>
                  </select>
                </Field>
                {maint.frequency === "weekly" && (
                  <Field label="Send on">
                    <select className={input} data-testid="maint-weekly-day" value={maint.weekly_day}
                      onChange={(e) => setMaint({ ...maint, weekly_day: Number(e.target.value) })}>
                      {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d, i) => <option key={d} value={i}>{d}</option>)}
                    </select>
                  </Field>
                )}
              </div>
            ) : <div className="text-slate-400 text-sm">Loading…</div>}
            <button onClick={saveMaint} data-testid="save-maintenance-btn"
              className="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-medium text-white transition-opacity duration-200 hover:opacity-90"
              style={{ backgroundColor: "var(--brand)" }}>
              <Save className="h-4 w-4" /> Save maintenance schedule
            </button>
          </div>
          <div className="surface-card p-6 space-y-5 max-w-3xl">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h2 className="font-display text-lg font-medium">Escalation & recap routing</h2>
                <p className="text-xs text-slate-400 mt-1">Each store's manager receives its cash-to-pocket escalations, leakage alerts, and a recap on its own day &amp; time set below.</p>
              </div>
              <button onClick={sendRecap} data-testid="send-recap-btn"
                className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium border border-[#2A3550] bg-[#1E2638] hover:border-[#38BDF8] text-slate-100 transition-colors duration-200">
                <Send className="h-4 w-4" /> Send weekly recap now
              </button>
            </div>
            <div className="space-y-2.5">
              {(routeStores || []).map((s) => (
                <div key={s.id} className="flex items-center gap-2 flex-wrap py-1.5 border-b border-[#232C40] last:border-0" data-testid={`route-row-${s.number}`}>
                  <div className="w-36 shrink-0 text-sm"><span className="font-medium">#{s.number}</span> <span className="text-slate-400">{s.name}</span></div>
                  <input type="email" defaultValue={s.manager_email || ""} data-testid={`manager-email-${s.number}`}
                    onChange={(e) => setEmails((p) => ({ ...p, [s.id]: e.target.value }))}
                    className="flex-1 min-w-[180px] rounded-lg bg-[#1E2638] border border-[#334063] px-3 py-2 text-sm text-slate-100 focus:border-[#38BDF8] focus:outline-none" placeholder="manager@store.com" />
                  <input type="number" defaultValue={s.leakage_threshold ?? 750} title="Leakage limit ($)" data-testid={`leak-${s.number}`}
                    onChange={(e) => setCfg((p) => ({ ...p, [s.id]: { ...p[s.id], leakage_threshold: e.target.value } }))}
                    className="w-24 rounded-lg bg-[#1E2638] border border-[#334063] px-3 py-2 text-sm text-slate-100 focus:border-[#38BDF8] focus:outline-none" />
                  <select defaultValue={s.recap_day ?? 0} title="Recap day" data-testid={`day-${s.number}`}
                    onChange={(e) => setCfg((p) => ({ ...p, [s.id]: { ...p[s.id], recap_day: e.target.value } }))}
                    className="rounded-lg bg-[#1E2638] border border-[#334063] px-2 py-2 text-sm text-slate-100 focus:border-[#38BDF8] focus:outline-none">
                    {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d, i) => <option key={d} value={i}>{d}</option>)}
                  </select>
                  <select defaultValue={s.recap_hour ?? 8} title="Recap hour (UTC)" data-testid={`hour-${s.number}`}
                    onChange={(e) => setCfg((p) => ({ ...p, [s.id]: { ...p[s.id], recap_hour: e.target.value } }))}
                    className="rounded-lg bg-[#1E2638] border border-[#334063] px-2 py-2 text-sm text-slate-100 focus:border-[#38BDF8] focus:outline-none">
                    {Array.from({ length: 24 }).map((_, h) => <option key={h} value={h}>{String(h).padStart(2,"0")}:00</option>)}
                  </select>
                  <button onClick={() => saveManager(s)} data-testid={`save-manager-${s.number}`}
                    className="rounded-lg px-4 py-2 text-sm font-medium text-white" style={{ backgroundColor: "var(--brand)" }}>Save</button>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Accounting options */}
        <TabsContent value="stations" className="mt-6">
          <StationRoles />
        </TabsContent>

        <TabsContent value="accounting" className="mt-6">
          <div className="surface-card p-6 space-y-5 max-w-3xl" data-testid="accounting-settings">
            <div className="flex items-center gap-2"><Calculator className="h-5 w-5 text-[#38BDF8]" /><h2 className="font-display text-lg font-medium">Accounting options</h2></div>
            <p className="text-xs text-slate-400">Send a clean reconciled-revenue &amp; leakage summary to your accountant, and switch on automatic delivery so it goes out every month without you lifting a finger.</p>
            {acct ? (
              <>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Accountant name">
                    <input className={input} data-testid="acct-name" value={acct.accountant_name || ""} onChange={(e) => setAcct({ ...acct, accountant_name: e.target.value })} />
                  </Field>
                  <Field label="Accountant email" hint="Where the summary is sent">
                    <input className={input} data-testid="acct-email" value={acct.accountant_email || ""} onChange={(e) => setAcct({ ...acct, accountant_email: e.target.value })} placeholder="accountant@firm.com" />
                  </Field>
                </div>
                <div className="flex items-center justify-between gap-4 rounded-lg border border-[#2A3550] bg-[#171E2E] px-4 py-3">
                  <div>
                    <div className="text-sm font-medium">Auto month-end summary</div>
                    <div className="text-xs text-slate-400">When on, the reconciled revenue &amp; leakage summary is emailed to your accountant automatically each month, on the day &amp; time you choose below.</div>
                  </div>
                  <Switch checked={!!acct.accountant_monthly} data-testid="acct-monthly-toggle" onCheckedChange={(v) => setAcct({ ...acct, accountant_monthly: v })} />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Send on day of month" hint="Days 29–31 fall back to the last day of shorter months">
                    <select className={input} data-testid="acct-day" value={acct.accountant_monthly_day ?? 1}
                      onChange={(e) => setAcct({ ...acct, accountant_monthly_day: Number(e.target.value) })}>
                      {Array.from({ length: 31 }).map((_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
                    </select>
                  </Field>
                  <Field label="Time (UTC)">
                    <select className={input} data-testid="acct-hour" value={acct.accountant_monthly_hour ?? 8}
                      onChange={(e) => setAcct({ ...acct, accountant_monthly_hour: Number(e.target.value) })}>
                      {Array.from({ length: 24 }).map((_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}
                    </select>
                  </Field>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <button onClick={saveAccounting} data-testid="save-accounting-btn"
                    className="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-medium text-white transition-opacity duration-200 hover:opacity-90"
                    style={{ backgroundColor: "var(--brand)" }}>
                    <Save className="h-4 w-4" /> Save accounting options
                  </button>
                  <button onClick={sendMonthlyNow} data-testid="send-monthly-now-btn"
                    className="inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium border border-[#2A3550] bg-[#1E2638] hover:border-[#38BDF8] text-slate-100 transition-colors duration-200">
                    <CalendarClock className="h-4 w-4" /> Send this month's summary now
                  </button>
                </div>
              </>
            ) : <div className="text-slate-400 text-sm">Loading…</div>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
